import {useEffect, useState} from 'react';
import {Box, Dialog, Typography} from '@mui/material';
import {AxiosError} from 'axios';
import {t} from 'i18next';
import AurButton from '../../../common/aur-button/AurButton';
import AurPizzaTrackerProgress from '../../../common/aur-pizza-traker-progress/AurPizzaTrackerProgress';
import {
  EvidenceProgressResponseType,
  getInitialIntakeProgressSteps,
  getUpdatedIntakeProgressSteps,
  IntakeProgressEventType,
  ProgressStepItemType,
} from './intake-progress-utils';
import {getEvidenceIntakeProcessStatus} from '../../../../api/EvidenceSourceUpload';
import AurLoadingIndicator from '../../../common/aur-loading-indicator/AurLoadingIndictor';
import {EvidenceParentRow} from '../../../../types/evidence-source/evidenceSource';
import {useToast} from '../../../../contexts/toastContext/ToastContext';
import {showErrorToast} from '../../../../api/utils';

interface IntakeProgressModalProps {
  clientId: string;
  projectId?: string;
  open: boolean;
  selectedRow: EvidenceParentRow;
  onClose: () => void;
}

const IntakeProgressModal = ({
  open,
  selectedRow,
  onClose,
  clientId,
  projectId,
}: IntakeProgressModalProps) => {
  const {showToast} = useToast();
  const [currentState, setCurrentState] =
    useState<IntakeProgressEventType | null>(null);
  const [progressStepData, setProgressStepData] = useState<
    ProgressStepItemType[]
  >(
    selectedRow?.custodianType == 'Multiple'
      ? getInitialIntakeProgressSteps(t)
      : getInitialIntakeProgressSteps(t).filter(
          (item) => item.status != 'Parse'
        )
  );
  const [isLoading, setIsLoading] = useState(false);
  const [uploadCompletion, setUploadCompletion] = useState<string>('');

  const fetchIntakeStatus = async () => {
    try {
      setIsLoading(true);
      const response = await getEvidenceIntakeProcessStatus({
        clientId: clientId,
        projectId: projectId ?? '',
        safeId: selectedRow?.safeID ?? '',
      });

      const {updatedStep, currentStep} = getUpdatedIntakeProgressSteps(
        progressStepData,
        response.data.data as EvidenceProgressResponseType
      );
      setProgressStepData(updatedStep);
      setCurrentState(currentStep);
      setUploadCompletion(response.data.data?.uponUploadCompletion || '');
    } catch (err) {
      showErrorToast(showToast, err as AxiosError, 'center');
      onClose();
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (open) fetchIntakeStatus();
  }, [open]);

  const getFormattedValue = (value?: string) => {
    return (value ?? '') !== '' ? value : '---';
  };
  return (
    <div>
      <Dialog
        fullScreen={true}
        open={open}
        PaperProps={{className: 'overlay'}}
        data-testid="intake-progress-modal"
        onClose={onClose}
      >
        <Box className="intake-progress-modal-container">
          <Box className="intake-header">
            <Typography
              className="typo-text headline2_size header-text"
              tabIndex={0}
            >
              {t('intakeProgressModal.header')}
            </Typography>
          </Box>
          <Box className="intake-content">
            <Box className="content-details">
              <Box className="details-column">
                <Box className="details-content" tabIndex={0}>
                  <Typography className="typo-text label2_size column-title">
                    {t('intakeProgressModal.columns.evidenceSourceName')}
                  </Typography>
                  <Typography className="typo-text body2_size">
                    {getFormattedValue(selectedRow?.evidenceSource)}
                  </Typography>
                </Box>
                <Box className="details-content" tabIndex={0}>
                  <Typography className="typo-text label2_size column-title">
                    {t('intakeProgressModal.columns.uploadCompletion')}
                  </Typography>
                  <Typography className="typo-text body2_size">
                    {getFormattedValue(uploadCompletion)}
                  </Typography>
                </Box>
              </Box>
              <Box className="details-column">
                <Box className="details-content" tabIndex={0}>
                  <Typography className="typo-text label2_size column-title">
                    {t('intakeProgressModal.columns.clientRefNumber')}
                  </Typography>
                  <Typography className="typo-text body2_size">
                    {getFormattedValue(selectedRow?.clientRef)}
                  </Typography>
                </Box>
                <Box className="details-content" tabIndex={0}>
                  <Typography className="typo-text label2_size column-title">
                    {t('intakeProgressModal.columns.dedupeMethod')}
                  </Typography>
                  <Typography className="typo-text body2_size">
                    {getFormattedValue(selectedRow?.deDupeMethod)}
                  </Typography>
                </Box>
              </Box>
              <Box className="details-column">
                <Box className="details-content" tabIndex={0}>
                  <Typography className="typo-text label2_size column-title">
                    {t('intakeProgressModal.columns.custodians')}
                  </Typography>
                  <Typography className="typo-text body2_size">
                    {selectedRow?.custodianType == 'Multiple'
                      ? 'Multi'
                      : getFormattedValue(
                          selectedRow?.custodianAssigned[0]?.name
                        )}
                  </Typography>
                </Box>
              </Box>
            </Box>
            <Box className="content-divider">
              <Box className="line" />
            </Box>
            <Box className="content-steps">
              {currentState && (
                <Typography className="typo-text headline3_size" tabIndex={0}>
                  {t('intakeProgressModal.progressLabel')}
                  {t(
                    `intakeProgressModal.label.${currentState?.toLowerCase()}`
                  )}
                </Typography>
              )}
              <Box className="steps-tracker">
                <AurPizzaTrackerProgress progressSteps={progressStepData} />
              </Box>
            </Box>
            <Typography
              className="typo-text body2_size multi-cust-desc-text"
              tabIndex={0}
            >
              {t('intakeProgressModal.multiCustDesc')}
            </Typography>
          </Box>

          <Box className="intake-button">
            <AurButton
              type="submit"
              label="Close"
              size="Medium"
              classNames="intake-close-button"
              onClick={onClose}
            />
          </Box>
        </Box>
      </Dialog>
      <AurLoadingIndicator isOpen={isLoading} overlay />
    </div>
  );
};

export default IntakeProgressModal;
